import React, {
  useState,
  useCallback,
  useRef,
  useImperativeHandle,
} from 'react';
import './style.scoped.css';

type TextInputProps = {
  label?: string;
  value?: string;
  rightText?: string;
  rightComponent?: React.ReactNode;
  helperText?: string;
  error?: boolean;
  onEnter?: (e: React.ChangeEvent) => void;
  isRequired?: boolean;
  disabled?: boolean;
  containerClassName?: string;
  placeholder?: string;
  useDisabledStyle?: boolean;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  maxLength?: number;
  inputMode?: string;
  onFocus?: () => void;
  onBlur?: (e: React.ChangeEvent) => void;
  type?: string;
  onKeyPress?: any;
  onKeyDown?: any;
  pattern?: string;
  onInput?: any;
};

const TextInput = React.memo(
  React.forwardRef((props: TextInputProps, ref) => {
    const {
      label,
      value,
      rightText,
      rightComponent,
      helperText,
      error,
      onEnter,
      isRequired = false,
      disabled,
      containerClassName,
      useDisabledStyle = false,
      onFocus,
      onBlur,
      onKeyDown,
      ...passProps
    } = props;
    const [focus, setFocus] = useState(false);
    const inputRef = useRef<HTMLInputElement>();

    useImperativeHandle(ref, () => ({
      focus: () => {
        inputRef.current.focus();
      },
    }));

    const _handleFocus = useCallback(() => {
      _doFocus(focus);
      if (onFocus) {
        onFocus();
      }
    }, [focus, onFocus]);

    const _handleBlur = useCallback(
      (e) => {
        _doBlur(focus);
        if (onBlur) {
          onBlur(e);
        }
      },
      [focus, onBlur]
    );

    const _handleClickInputContainer = useCallback(() => {
      if (disabled) {
        return;
      }
      _doFocus(focus);
    }, [focus, disabled]);

    const _doFocus = (isFocus) => {
      if (!isFocus) {
        setFocus(true);
        inputRef.current.focus();
      }
    };

    const _doBlur = (isFocus) => {
      if (isFocus) {
        setFocus(false);
      }
    };

    const _getInputBorderClassName = () => {
      if (error) return 'border-error';
      return focus ? 'border-secondary ring-1 ring-secondary' : 'border-border';
    };

    const getKeyCode = useCallback(function (str) {
      return str.charCodeAt(str.length - 1);
    }, []);

    const _handleKeyDown = (e) => {
      const value = e.target.value;
      let keyCode = e?.keyCode || e?.which;
      onKeyDown && onKeyDown(e);
      if (keyCode == 0 || keyCode == 229) {
        //for android chrome keycode fix
        keyCode = getKeyCode(value);
      }
      if (keyCode == 13) {
        if (onEnter) {
          onEnter(e);
        } else {
          e.target.blur();
        }
      }
    };

    return (
      <div className={containerClassName}>
        {!!label && (
          <div className="labelText mb-2">
            {label}
            {isRequired ? (
              <label className="bodyText text-error"> *</label>
            ) : (
              ''
            )}
          </div>
        )}
        <div
          className={`${_getInputBorderClassName()} ${
            disabled || useDisabledStyle
              ? 'textInputContainerDisabled'
              : 'textInputContainer'
          }`}
          onClick={_handleClickInputContainer}
        >
          <input
            onFocus={_handleFocus}
            onBlur={_handleBlur}
            className={
              disabled || useDisabledStyle ? 'textInputDisable' : 'textInput'
            }
            ref={inputRef}
            onKeyDown={_handleKeyDown}
            disabled={disabled}
            value={value}
            {...passProps}
          />
          {!!rightText && (
            <div className="text-base text-onSurfaceVariant font-semibold ml-3">
              {rightText}
            </div>
          )}
          {!!rightComponent && rightComponent}
        </div>
        {!!helperText && (
          <div className={`${error ? 'helperTextError' : 'helperText'}`}>
            {helperText}
          </div>
        )}
      </div>
    );
  })
);

export const useTextInputValue = (defaultValue: string) => {
  const [value, setValue] = useState<string>(defaultValue);
  const onChange = useCallback((event) => {
    setValue(event.target.value);
  }, []);
  return [value, onChange];
};

export default TextInput;
